import { Form, Input } from 'antd';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { IApiGetTicketsParam, IApiReportCreditCard } from 'features/ticketList/services/types/api';
import { ICreditCardFilterFunction } from 'features/ticketList/services/types/creditCard';
import moment from 'moment';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import DatePicker from './components/TabsV2/Filter/DatePicker';
import DateType from './components/TabsV2/Filter/DateType';
import btn_search_icon from '../ListTicketPage/components/TabsV2/Filter/search.svg';
import search_icon from '../ListTicketPage/icons/search.svg';
import Button from 'components/Button';
import { useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';
import ChoosePrintStationModal from './CreditCardDailyReport/ChoosePrintStationModal';
import shopSelectors from 'services/shop/selectors';
import Box from 'components/Box';
const inputStyles: CSSProperties = { minWidth: '24rem' };

interface IFormSearchValues {
  viewType: string;
  dates: IFormSearchDatesValue;
  last4Num: string;
  ticketNumber: string;
  staffId: string;
  paymentType: string;
}

interface IFormSearchDatesValue {
  startTime: string;
  endTime: string;
}
const getViewType = (params: IApiGetTicketsParam) => {
  if (params.startDate === params.endDate) return CALENDAR_VIEW_TYPE.DAY_VIEW;
  const monthDate = moment(params.startDate, 'MM-DD-YYYY');
  if (
    monthDate.startOf('month').format('MM-DD-YYYY') === params.startDate &&
    monthDate.endOf('month').format('MM-DD-YYYY') === params.endDate
  )
    return CALENDAR_VIEW_TYPE.MONTH_VIEW;
  return CALENDAR_VIEW_TYPE.WEEK_VIEW;
};
type Props = {
  onFilter?: ICreditCardFilterFunction;
};
const CreditCardFilter = ({ onFilter = () => undefined }: Props) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openChoosePrintStationModal, setOpenChoosePrintStationModal] = useState(false);
  const allSetting = shopSelectors.data.allSetting();
  const creditCardParam = ticketListSelectors.creditCardTicketsTableData.params();
  const viewType = Form.useWatch('viewType', form);
  const startDate = Form.useWatch(['dates', 'startTime'], form);

  const initialValues = useMemo(
    () => ({
      viewType: CALENDAR_VIEW_TYPE.DAY_VIEW,
      dates: {
        startTime: creditCardParam.startDate,
        endTime: creditCardParam.endDate,
      },
    }),
    [],
  );

  useEffect(() => {
    const currentViewType = form.getFieldValue('viewType');
    const currentDates = form.getFieldValue('dates');
    const dates = {
      startTime: creditCardParam.startDate,
      endTime: creditCardParam.endDate,
    };
    if (
      currentDates.startTime === dates.startTime &&
      currentDates.endTime === dates.endTime
    ) {
      return form.setFieldsValue({
        dates,
        viewType: currentViewType,
      });
    }
    form.resetFields();
    form.setFieldsValue({
      dates,
      viewType: getViewType(creditCardParam),
    });
  }, [creditCardParam]);

  const handleFinish = (values: any) => {
    const data = values as IFormSearchValues;
    const params: IApiReportCreditCard = {
      startDate: data.dates.startTime,
      endDate: data.dates.endTime,
      page: 1,
      last4: data.last4Num ? data.last4Num : undefined,
      ticketNumber: data.ticketNumber ? data.ticketNumber : undefined,
    };
    onFilter({
      startDate: params.startDate || '',
      endDate: params.endDate || '',
    });
    dispatch(ticketListActions.setCreditCardParams(params));
  };

  const handleOpenChoosePrintStationModal = () => {
    setOpenChoosePrintStationModal(true);
  };

  const handlePrintSummary = () => {
    navigate(`/store/${storage.shop_id.get()}/credit-cards/print-summary`, {
      state: {
        startDate,
      },
    });
  };

  return (
    <Container
      form={form}
      onFinish={handleFinish}
      initialValues={initialValues}
    >
      <Form.Item name={'dates'} noStyle>
        <DateSearch />
      </Form.Item>
      <Form.Item noStyle name={'last4Num'} />
      <Form.Item noStyle name={'last4Num'}>
        <Last4NumInput style={inputStyles} />
      </Form.Item>
      <Form.Item noStyle name={'ticketNumber'}>
        <TicketNoInput style={inputStyles} />
      </Form.Item>
      <ButtonSearch />
      {allSetting?.displayPrintDailyCreditCard && (
        <>
          <Button
            cardCashier
            ntype='SECONDARY'
            className='btn-print'
            icon='print'
            onClick={handleOpenChoosePrintStationModal}
            disabled={viewType !== CALENDAR_VIEW_TYPE.DAY_VIEW}
          >
            Daily Batch Report
          </Button>
          <Button
            cardCashier
            ntype='LIGHT_BLUE'
            className='btn-print'
            icon='print'
            onClick={handlePrintSummary}
            disabled={viewType !== CALENDAR_VIEW_TYPE.DAY_VIEW}
          >
            Summary Daily Batch Report
          </Button>
        </>
      )}
      <ChoosePrintStationModal
        open={openChoosePrintStationModal}
        onClose={() => setOpenChoosePrintStationModal(false)}
        startDate={startDate}
      />
    </Container>
  );
};

export default CreditCardFilter;
const ButtonSearch = () => (
  <button type='submit' className='btn-search'>
    <img
      src={btn_search_icon}
      alt='btn_search_icon'
      style={{ height: 24, width: 24 }}
    />
    <span>Search</span>
  </button>
);

const Container = styled(Form).attrs({
  className: 'ticket-filter-v2-container',
})`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  .ant-select .ant-select-selector .ant-select-selection-placeholder {
    color: #1d2129;
  }
  .ant-select .ant-select-selector .ant-select-selection-placeholder,
  .ant-select-selection-item,
  .ant-input-affix-wrapper > input.ant-input {
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  button.btn-search {
    display: flex;
    height: 56px;
    padding: 0px 24px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    background: #FF8890;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4),
      0px 4px 8px -3px rgba(45, 35, 66, 0.3), 0px -4px 0px 0px #E7646D inset;
    span {
      color: #fff;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .btn-print {
    .CONTENT_2 {
      color: #1d2129;
      text-align: center;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;

type DateSearchProps = {
  value?: { startTime: string; endTime: string };
  onChange?: (value: { startTime: string; endTime: string }) => void;
  quickView?: boolean;
};
const ViewType = ({
  quickView,
  onChange,
  value,
  onChangeDate,
}: {
  onChangeDate: DateSearchProps['onChange'];
  quickView?: boolean;
  value?: CALENDAR_VIEW_TYPE;
  onChange?: (value: CALENDAR_VIEW_TYPE) => void;
}) => (
  <DateType
    twoWeeks={!quickView}
    v2
    viewType={value}
    onChangeValue={onChangeDate}
    onChangeViewType={onChange}
  />
);
const DateSearch = ({
  quickView,
  value,
  onChange = () => undefined,
}: DateSearchProps) => {
  return (
    <Box display='flex' alignItems='center' gap='3'>
      <Form.Item name={'viewType'} noStyle>
        <ViewType quickView={quickView} onChangeDate={onChange} />
      </Form.Item>
      <Form.Item shouldUpdate dependencies={['viewType']} noStyle>
        {({ getFieldValue }) => (
          <DatePicker
            v2
            value={value}
            onChangeValue={onChange}
            viewType={getFieldValue('viewType')}
          />
        )}
      </Form.Item>
    </Box>
  );
};

const searchSuffix = (
  <img src={search_icon} alt='search_icon' style={{ height: 24, width: 24 }} />
);
const TicketNoInput = ({
  value,
  onChange = () => undefined,
  style,
}: {
  style?: CSSProperties;
  value?: string;
  onChange?: (val: string) => void;
}) => {
  return (
    <Input
      className='ticketNumberInput'
      placeholder='Ticket No.'
      type='number'
      value={value}
      onChange={(e) => {
        if (e.target.value.length > 4) return e.preventDefault();
        onChange(e.target.value);
      }}
      suffix={searchSuffix}
      style={style}
    />
  );
};

const Last4NumInput = ({
  value,
  onChange = () => undefined,
  style,
}: {
  style?: CSSProperties;
  value?: string;
  onChange?: (val: string) => void;
}) => {
  return (
    <Input
      className='ticketNumberInput'
      placeholder='Last 4 Num'
      type='number'
      value={value}
      onChange={(e) => {
        if (e.target.value.length > 4) return e.preventDefault();
        onChange(e.target.value);
      }}
      suffix={searchSuffix}
      style={style}
    />
  );
};
